import { Modal, Group, Box, Text, LoadingOverlay } from "@mantine/core";
import { Button } from "@xplatform/design-system";
import {
    IconChevronRight,
    IconChevronLeft,
    IconCheck,
} from "@tabler/icons-react";
import { useFlowConfig } from "@hooks/flow";
import { useFlowWizard } from "@hooks/flow/useFlowWizard";
import { useTranslation } from "@hooks/useTranslation";
import { useToast } from "@hooks/useToast";
import type { FlowConfigViewModel } from "@models/doctype";
import {
    buildCreateFlowConfigRequest,
    type FlowConfigFormData,
} from "@utils/builders/flowConfig";
import { FlowTypeSelector } from "./FlowTypeSelector";
import { FlowStepStepper } from "./FlowStepStepper";
import { StepConfiguration } from "./StepConfiguration";
import "./CreateFlowConfigDialog.scss";

interface CreateFlowConfigDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    docTypeId: string;
    existingConfig?: FlowConfigViewModel | null;
}

export const CreateFlowConfigDialog = ({
    open,
    onClose,
    onSuccess,
    docTypeId,
    existingConfig,
}: CreateFlowConfigDialogProps) => {
    const { t } = useTranslation();
    const { createFlowConfig, isLoading } = useFlowConfig();
    const { showSuccess, showError } = useToast();

    const {
        activeStep,
        activeTab,
        flowType,
        flowDescription,
        steps,
        isFlowTypeSelected,
        providers,
        models,
        selectedProvider,
        selectedModel,
        isLoadingModels,
        isLoadingProviders,
        llmProviders,
        llmModels,
        setActiveTab,
        setFlowType,
        setFlowDescription,
        setIsFlowTypeSelected,
        handleProviderChange,
        handleModelChange,
        updateCurrentStepConfig,
        isStepValid,
        canProceed,
        canSubmit,
        handleNext,
        handleBack,
        loadAgentModels,
        updateAgents,
    } = useFlowWizard({ open, existingConfig });

    const handleSubmit = async () => {
        if (!canSubmit()) {
            showError("Please configure all steps before saving");
            return;
        }

        const formData: FlowConfigFormData = {
            flowBaseId: existingConfig?.flowBaseId || null,
            flowType,
            flowDescription: flowDescription || null,
            steps,
        };

        const request = buildCreateFlowConfigRequest(formData);
        const result = await createFlowConfig(docTypeId, request);

        if (result) {
            showSuccess(t("admin.flowConfigs.messages.saveSuccess"));
            onClose();
            onSuccess?.();
        } else {
            showError(t("admin.flowConfigs.messages.saveFailed"));
        }
    };

    const currentStep = steps[activeStep];

    return (
        <Modal
            opened={open}
            onClose={onClose}
            title={
                <Text fw={600} size="lg">
                    {existingConfig
                        ? "Create New Flow Version"
                        : "Create Flow Configuration"}
                </Text>
            }
            size="90%"
            className="create-flow-config-dialog"
            closeButtonProps={{ disabled: isLoading }}
            portalProps={{ target: "#admin-portal-root" }}
            styles={{
                content: {
                    maxHeight: "calc(100vh - 60px)",
                    display: "flex",
                    flexDirection: "column",
                },
                header: {
                    flexShrink: 0,
                },
                body: {
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                },
            }}
        >
            <LoadingOverlay visible={isLoading} />

            <Box className="create-flow-config-dialog__content">
                {!existingConfig && !isFlowTypeSelected && (
                    <FlowTypeSelector
                        flowType={flowType}
                        flowDescription={flowDescription}
                        onFlowTypeChange={setFlowType}
                        onDescriptionChange={setFlowDescription}
                    />
                )}

                {isFlowTypeSelected && steps.length > 0 && (
                    <>
                        <FlowStepStepper
                            steps={steps}
                            activeStep={activeStep}
                            isStepValid={isStepValid}
                        />

                        {currentStep && (
                            <StepConfiguration
                                step={currentStep}
                                stepNumber={activeStep + 1}
                                activeTab={activeTab}
                                providers={providers}
                                models={models}
                                selectedProvider={selectedProvider}
                                selectedModel={selectedModel}
                                isLoadingModels={isLoadingModels}
                                isLoadingProviders={isLoadingProviders}
                                onTabChange={setActiveTab}
                                onProviderChange={handleProviderChange}
                                onModelChange={handleModelChange}
                                onFieldChange={updateCurrentStepConfig}
                                llmProviders={llmProviders}
                                llmModels={llmModels}
                                onAgentsChange={updateAgents}
                                onLoadAgentModels={loadAgentModels}
                            />
                        )}
                    </>
                )}
            </Box>

            <Group
                className="create-flow-config-dialog__actions"
                justify="space-between"
                mt="md"
            >
                <Button variant="subtle" onClick={onClose} disabled={isLoading}>
                    {t("common.cancel")}
                </Button>
                <Group gap="sm">
                    {!isFlowTypeSelected && (
                        <Button
                            onClick={() => {
                                if (flowType) {
                                    setIsFlowTypeSelected(true);
                                }
                            }}
                            disabled={!flowType || isLoading}
                            rightSection={<IconChevronRight size={16} />}
                        >
                            Continue
                        </Button>
                    )}

                    {isFlowTypeSelected && (
                        <>
                            <Button
                                variant="default"
                                onClick={() => {
                                    if (activeStep === 0) {
                                        setIsFlowTypeSelected(false);
                                    } else {
                                        handleBack();
                                    }
                                }}
                                disabled={isLoading}
                                leftSection={<IconChevronLeft size={16} />}
                            >
                                Back
                            </Button>
                            {activeStep < steps.length - 1 ? (
                                <Button
                                    onClick={handleNext}
                                    disabled={!canProceed() || isLoading}
                                    rightSection={<IconChevronRight size={16} />}
                                >
                                    Next
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!canSubmit() || isLoading}
                                    leftSection={<IconCheck size={16} />}
                                >
                                    {t("common.save")}
                                </Button>
                            )}
                        </>
                    )}
                </Group>
            </Group>
        </Modal>
    );
};
